import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  Button,
  Typography,
  useMediaQuery,
  Box,
  CircularProgress,
  MenuItem,
  Card,
  CardContent,
  Fade,
  Grid, 
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import formFields from "./formFields";
import { POST_FORM } from "../../api/form";

const Form = ({ open = true, onClose, category, type = "popup" }) => {
  const isMobile = useMediaQuery("(max-width:768px)");

  const fields = formFields[category]?.fields;
  const image = formFields[category]?.image;
  const heading = formFields[category]?.heading;
  
  const [formData, setFormData] = useState(
    fields?.reduce((acc, field) => ({ ...acc, [field.name]: "" }), {})
  );
  const [otherFields, setOtherFields] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleOtherChange = (fieldName, value) => {
    setOtherFields({ ...otherFields, [fieldName]: value });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        category,
        ...formData,
        ...Object.keys(otherFields).reduce((acc, key) => {
          if (formData[key] === "Other") acc[key] = otherFields[key];
          return acc;
        }, {}),
      };
      await fetch(POST_FORM, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      setSubmitted(true);
    } catch (err) {
      alert("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const FormContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        padding: type === "popup" ? { xs: "10px", md: "20px" } : "0px",
        position: "relative",
        alignItems: "stretch",
        gap: { xs: 3, md: 5 }
      }}
    >
      {type === "popup" && (
        <IconButton
          onClick={onClose}
          sx={{ 
            position: "absolute", 
            top: isMobile ? -10 : 0, 
            right: isMobile ? -10 : 0, 
            color: "#94a3b8",
            zIndex: 10
          }}
        >
          <CloseIcon />
        </IconButton>
      )}

      {!isMobile && !submitted && image && (
        <Box
          sx={{
            width: category === "Main Apply as Mentor" ? "55%" : "45%",
            flexShrink: 0,
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
          }}
        >
          <img
            src={image}
            alt="form visual"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        </Box>
      )}

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", py: type === "popup" ? 2 : 0 }}>
        {!submitted ? (
          <>
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 800, color: "#0f172a", textAlign: isMobile ? "center" : "left", lineHeight: 1.2 }}>
              {heading}
            </Typography>
            <Typography variant="body2" sx={{ mb: 4, color: "#64748b", textAlign: isMobile ? "center" : "left", fontWeight: 500 }}>
              Please fill in your details and we will get back to you shortly.
            </Typography>

            {/* --- FIELDS GRID CONTAINER --- */}
            <Grid container spacing={2}>
              {fields.map((field) => {
                const isNameField = field.name.toLowerCase().match(/first|last|second|fname|lname/i) && 
                                   !field.name.toLowerCase().includes('full');
                
                return (
                  <Grid item key={field.name} xs={12} sm={isNameField ? 6 : 12}>
                    {field.type === "dropdown" ? (
                      <>
                        <TextField
                          select
                          name={field.name}
                          label={field.label}
                          required={field.required}
                          fullWidth
                          size="small"
                          value={formData[field.name]}
                          onChange={handleChange}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        >
                          {field.options?.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                          <MenuItem value="Other">Other</MenuItem>
                        </TextField>
                        {formData[field.name] === "Other" && (
                          <Fade in={true}>
                            <TextField
                              label={`Enter ${field.label}`}
                              value={otherFields[field.name] || ""}
                              onChange={(e) => handleOtherChange(field.name, e.target.value)}
                              fullWidth
                              size="small"
                              sx={{ mt: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                            />
                          </Fade>
                        )}
                      </>
                    ) : (
                      <TextField
                        name={field.name}
                        label={field.label}
                        required={field.required}
                        value={formData[field.name]}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        multiline={field.type === "textarea"}
                        rows={field.type === "textarea" ? 3 : 1}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                      />
                    )}
                  </Grid>
                );
              })}

              {/* Submit Button */}
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                  sx={{
                    borderRadius: 2,
                    padding: "12px",
                    backgroundColor: "#8257E6",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "1rem",
                    textTransform: "none",
                    boxShadow: "0 10px 20px -5px rgba(130, 87, 230, 0.3)",
                    mt: 1,
                    "&:hover": {
                      backgroundColor: "#6D48C4",
                      boxShadow: "0 15px 25px -5px rgba(130, 87, 230, 0.4)",
                    },
                  }}
                >
                  {loading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Submit Request"}
                </Button>
              </Grid>
            </Grid>
          </>
        ) : (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#8257E6", mb: 1 }}>Success! ✅</Typography>
            <Typography variant="body1" sx={{ color: "#475569", fontWeight: 500 }}>
              Thank you for responding. Our team will contact you soon.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  return type === "popup" ? (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md"
      PaperProps={{ sx: { borderRadius: 4, p: 2, boxShadow: "0 25px 50px -12px rgba(130, 87, 230, 0.25)" } }}
    >
      <DialogContent>{FormContent}</DialogContent>
    </Dialog>
  ) : (
    <Card sx={{ maxWidth: "1000px", margin: "auto", mt: 4, p: { xs: 2, md: 4 }, boxShadow: "0 20px 50px -12px rgba(130, 87, 230, 0.15)", borderRadius: 4, border: "1px solid rgba(226, 232, 240, 0.7)" }}>
      <CardContent>{FormContent}</CardContent>
    </Card>
  );
};

export default Form;